/**
 * @Author: v_jppjpeng
 * @Date: 2022-10-16 16:43:33
 * @Explain: 选择时间
 */
import { Icon, Select } from '@/components/tdesign';
import { intlGet } from '@/locales';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { DatePicker } from 'tdesign-react';
// utils
import {
  AbbreviationLanguageEnum,
  LanguageEnum,
  StoreKeyEnum,
} from '@/definition/common/data.d';
import { setTimeOut } from '@/utils/common';
import {
  formattedTime,
  generateTimeData,
  getTimeHms,
  onSplicingTime,
  scDateFormat,
} from '@/utils/format/time';
import { getLocalStorage } from '@/utils/global';

type PropsType = {
  selectProps?: any;
  /** 是否显示日期 */
  isShowDate?: boolean;
  /** 是否显示时分 */
  isShowTime?: boolean;
  /** 分钟间隔 */
  minuteInterval?: number;
  /** 图标 */
  suffixIcon?: any;
  // 以下直接注入DatePicker
  className?: string;
  disabled?: boolean;
  value?: any;
  /** 日期占位语 */
  datePlaceholder?: string;
  /** 时间占位语 */
  timePlaceholder?: string;
  /** 延迟触发change */
  timeoutChangeNum?: number;
  onChange?: (value: any) => void;
  /** 日期显示的格式  */
  dateShowFormat?: string;
  /** 禁用日期（透传给 DatePicker，支持函数或 { before, after } 对象） */
  disableDate?: any;
};

function CustomDatePicker(props: PropsType) {
  const {
    selectProps,
    isShowDate = true,
    isShowTime,
    value,
    disabled,
    minuteInterval,
    suffixIcon = <Icon.Font name="date-picker" fontSize={24} />,
    datePlaceholder = intlGet('COMMON.SELECT.DATE'),
    timePlaceholder = intlGet('COMMON.SELECT.TIME'),
    timeoutChangeNum = 0,
    onChange,
    dateShowFormat,
  } = props;
  const plugInLanguage = getLocalStorage(StoreKeyEnum.DESIGN_LANGUAGE);

  // 时分选择
  const timeOptions = generateTimeData({ minuteInterval });
  // 日期选择框 - 值
  const [datePickerValue, setDatePickerValue] = useState('');

  const timeVal = useMemo(() => {
    if (value) {
      return getTimeHms(value);
    }
  }, [value]);

  // 仅用于回显时分信息
  const selectValue = useMemo(() => {
    let newValue = value;
    if (value) {
      newValue = getTimeHms(value);
      // 下拉选中中包含newValue 则直接返回
      if (timeOptions.some((i) => i.value === newValue)) {
        return newValue;
      }

      // 获取存储语言设置，例如zh-CN
      const localLanguage = getLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE);
      const formatStr =
        localLanguage === LanguageEnum.ZH ? 'a hh:mm' : 'hh:mm a';

      // 下拉选中中不包含newValue 则需要将时分秒格式化为12小时制返回
      newValue = moment(`1970-01-01 ${newValue}`).format(formatStr);
      return formattedTime(newValue);
    }
  }, [value, timeOptions]);

  // // 修复英文环境 年-月-日信息回显异常
  // // 修复方法：英文环境，将”年-月-日“调整顺序为"日-月-年"
  // const newValue = useMemo(() => {
  //   try {
  //     const plugInLanguage = getLocalStorage(StoreKeyEnum.DESIGN_LANGUAGE);
  //     const valueBefore = value?.split(' ')?.[0] || '';
  //     let outputDate = '';
  //     if (valueBefore) {
  //       const [year, month, day] = valueBefore.split('-');
  //       outputDate =
  //         plugInLanguage === AbbreviationLanguageEnum.EN
  //           ? `${day}-${month}-${year}`
  //           : value;
  //     }
  //     return outputDate;
  //   } catch (e) {
  //     return value;
  //   }
  // }, [value]);

  // 修复英文环境 年-月-日信息回显异常
  // 修复方法：英文环境，将”年-月-日“调整顺序为"日-月-年"
  // 2026-03-16 - 需求变更 - 英文调整显示“年-月-日”
  useEffect(() => {
    let newVal = '';
    try {
      if (value && plugInLanguage === AbbreviationLanguageEnum.EN) {
        const valueBefore = value?.split(' ')?.[0] || '';
        // Use moment to parse the YYYY-MM-DD value and format it for display
        const m = moment(valueBefore, 'YYYY-MM-DD');
        if (m.isValid()) {
          newVal = m.format(dateShowFormat || 'DD-MM-YYYY');
        } else {
          newVal = value;
        }
      } else {
        newVal = value || '';
      }
    } catch (e) {
      newVal = value;
    }
    setDatePickerValue(newVal);
  }, [value, dateShowFormat, plugInLanguage]);

  return (
    <div className="td-custom-date-picker">
      {/* 日期选择 */}
      {isShowDate && (
        <DatePicker
          {...props}
          value={datePickerValue}
          format={dateShowFormat || scDateFormat({ showHMS: false })}
          className="mr-10"
          suffixIcon={suffixIcon}
          placeholder={datePlaceholder}
          onChange={(val: any) => {
            const actualFormat =
              dateShowFormat || scDateFormat({ showHMS: false });
            const m = moment(val, actualFormat);
            const outputDate = m.isValid() ? m.format('YYYY-MM-DD') : val;
            onChange?.(onSplicingTime(outputDate, timeVal));
          }}
        />
      )}

      {/* 时分选择 */}
      {isShowTime && (
        <Select
          disabled={disabled}
          clearable={false}
          placeholder={timePlaceholder}
          options={timeOptions.map((item) => ({
            label: item.text,
            value: item.value,
          }))}
          {...selectProps}
          size="m"
          value={selectValue}
          onChange={(val: string) => {
            onChange?.(onSplicingTime(value, val));
          }}
        />
      )}
    </div>
  );
}

export default CustomDatePicker;
